import React, { useState, useRef } from 'react';
import Tooltip from '../common/Tooltip';

interface GridItem {
  id: number;
  title: string;
  type: 'project' | 'department' | 'process' | 'risk';
  status: 'Active' | 'Inactive' | 'Draft' | 'Review';
  description: string;
  owner: string;
  updated: string;
  priority?: 'High' | 'Medium' | 'Low';
}

const CardGridTest: React.FC = () => {
  const [viewMode, setViewMode] = useState<'grid_2' | 'grid_3' | 'grid_4'>('grid_3');
  const [cardSize, setCardSize] = useState<'large' | 'medium' | 'small' | 'compact'>('medium');
  
  const [gridItems] = useState<GridItem[]>([
    { 
      id: 1, 
      title: 'Risk Assessment Framework', 
      type: 'process', 
      status: 'Active', 
      description: 'Comprehensive framework for organizational risk assessment and mitigation strategies.',
      owner: 'John Anderson',
      updated: '2025-01-15',
      priority: 'High'
    },
    { 
      id: 2, 
      title: 'IT Department Governance', 
      type: 'department', 
      status: 'Review', 
      description: 'Technology governance policies and compliance requirements for IT operations.',
      owner: 'Sarah Mitchell',
      updated: '2025-01-12',
      priority: 'Medium'
    },
    { 
      id: 3, 
      title: 'Digital Transformation Project', 
      type: 'project', 
      status: 'Active', 
      description: 'Enterprise-wide digital transformation initiative with governance oversight.',
      owner: 'Mike Chen',
      updated: '2025-01-10',
      priority: 'High'
    },
    { 
      id: 4, 
      title: 'Data Privacy Compliance', 
      type: 'risk', 
      status: 'Draft', 
      description: 'GDPR and regulatory compliance framework for data protection.',
      owner: 'Lisa Rodriguez',
      updated: '2025-01-08',
      priority: 'High'
    },
    { 
      id: 5, 
      title: 'Financial Reporting Process', 
      type: 'process', 
      status: 'Active', 
      description: 'Standardized financial reporting workflow with audit controls.',
      owner: 'David Kumar',
      updated: '2025-01-05',
      priority: 'Medium'
    },
    { 
      id: 6, 
      title: 'HR Compliance Review', 
      type: 'department', 
      status: 'Inactive', 
      description: 'Human resources policy compliance and governance review process.',
      owner: 'Emma Thompson',
      updated: '2024-12-28',
      priority: 'Low'
    },
    { 
      id: 7, 
      title: 'Vendor Management System', 
      type: 'project', 
      status: 'Review', 
      description: 'Third-party vendor assessment and management governance framework.',
      owner: 'Robert Wilson',
      updated: '2024-12-25',
      priority: 'Medium'
    },
    { 
      id: 8, 
      title: 'Cybersecurity Risk Matrix', 
      type: 'risk', 
      status: 'Active', 
      description: 'Information security risk assessment and threat analysis framework.',
      owner: 'Jennifer Liu',
      updated: '2024-12-22',
      priority: 'High'
    }
  ]);

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Active': return 'visual-status-success';
      case 'Draft': return 'visual-status-warning';
      case 'Review': return 'visual-status-info';
      case 'Inactive': return 'visual-status-inactive';
      default: return 'visual-status-info';
    }
  };

  const getPriorityClass = (priority?: string) => {
    switch (priority) {
      case 'High': return 'visual-status-danger';
      case 'Medium': return 'visual-status-warning';
      case 'Low': return 'visual-status-info';
      default: return 'visual-status-info';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'project': return '🚀';
      case 'department': return '🏢';
      case 'process': return '⚙️';
      case 'risk': return '⚠️';
      default: return '📄';
    }
  };

  // Tooltip state + anchor ref used for the css info tooltip
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const infoAnchorRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div>
      <div className="css-info">
        <div>Card Grid Layout Pattern</div>
        <button
          ref={infoAnchorRef}
          className="css-info-icon"
          aria-label="CSS info"
          title="CSS info"
          aria-expanded={tooltipVisible}
          aria-controls="css-tooltip-cardgrid"
          onClick={() => setTooltipVisible(v => !v)}
        >
          <span className="sr-only">CSS info</span>
        </button>
        <Tooltip anchorRef={infoAnchorRef} isOpen={tooltipVisible} onClose={() => setTooltipVisible(false)} id="css-tooltip-cardgrid">
          <div>
            <strong>CSS Classes Used:</strong>
            <div style={{ marginTop: 8 }}>
              .layout-grid-2col/.layout-grid-3col/.layout-grid-4col - Grid systems<br/>
              .visual-card-large/.visual-card-medium/.visual-card-small/.visual-card-compact - Card sizes<br/>
              .visual-card-header - Card header styling<br/>
              .visual-status-success/.visual-status-warning/.visual-status-info - Status badges<br/>
              .interactive-hover-lift - Hover animations<br/>
              .text-heading-medium/.text-label-primary - Typography<br/>
              .container-toolbar - Control toolbar
            </div>
          </div>
        </Tooltip>
      </div>

      {/* Controls Toolbar */}
      <div className="container-toolbar">
        <div className="layout-flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="layout-flex-row" style={{ gap: '16px' }}>
            <div className="layout-flex-row" style={{ gap: '8px', alignItems: 'center' }}>
              <span className="text-label-primary">Grid:</span>
              <select 
                value={viewMode} 
                onChange={(e) => setViewMode(e.target.value as any)}
                className="control-form-select"
                style={{ fontSize: '12px', padding: '4px 8px' }}
              >
                <option value="grid_2">2 Columns</option>
                <option value="grid_3">3 Columns</option>
                <option value="grid_4">4 Columns</option>
              </select>
            </div>
            <div className="layout-flex-row" style={{ gap: '8px', alignItems: 'center' }}>
              <span className="text-label-primary">Card Size:</span>
              <select 
                value={cardSize} 
                onChange={(e) => setCardSize(e.target.value as any)}
                className="control-form-select"
                style={{ fontSize: '12px', padding: '4px 8px' }}
              >
                <option value="large">Large (240px)</option>
                <option value="medium">Medium (200px)</option>
                <option value="small">Small (180px)</option>
                <option value="compact">Compact (160px)</option>
              </select>
            </div>
          </div>
          <div className="layout-flex-row" style={{ gap: '8px' }}>
            <button className="btn-secondary-ghost">
              🔍 Filter
            </button>
            <button className="btn-secondary-ghost">
              📊 Sort
            </button>
            <button className="btn-add-primary">
              ➕ Add Item
            </button>
          </div>
        </div>
      </div>

      {/* Grid Container */}
      <div className={`layout-${viewMode}`}>
        {gridItems.map((item) => (
          <div
            key={item.id}
            className={`visual-card-${cardSize} interactive-hover-lift`}
          >
            {/* Card Header */}
            <div className="visual-card-header">
              <div className="layout-flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="layout-flex-row" style={{ gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '16px' }}>{getTypeIcon(item.type)}</span>
                  <span className="text-code-mono" style={{ textTransform: 'uppercase' }}>
                    {item.type}
                  </span>
                </div>
                <div className="layout-flex-row" style={{ gap: '4px' }}>
                  <span className={getStatusClass(item.status)}>
                    {item.status}
                  </span>
                  {item.priority && (
                    <span className={getPriorityClass(item.priority)}>
                      {item.priority}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Card Content */}
            <div className="layout-card-content">
              <h3 className="text-heading-medium" style={{ lineHeight: '1.3' }}>
                {item.title}
              </h3>
              
              <p className={`text-label-primary layout-card-description layout-card-description--${cardSize}`}>
                {item.description}
              </p>

              <div className="layout-card-footer">
                <div>
                  <div className="text-label-muted" style={{ fontSize: '11px' }}>Owner</div>
                  <div className="text-label-primary" style={{ fontSize: '12px', fontWeight: '500' }}>
                    {item.owner}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="text-label-muted" style={{ fontSize: '11px' }}>Updated</div>
                  <div className="text-code-mono" style={{ fontSize: '11px' }}>
                    {item.updated}
                  </div>
                </div>
              </div>

              {/* Card Actions */}
              <div className="layout-card-actions">
                <button className="btn-icon-only" title="View Details">
                  👁️
                </button>
                <button className="btn-icon-only" title="Edit">
                  ✏️
                </button>
                <button className="btn-icon-only" title="Share">
                  🔗
                </button>
                <div className="layout-card-actions-more">
                  <button className="btn-icon-only" title="More Actions">
                    ⋯
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CardGridTest;